import { useQuery } from '@apollo/client';
import { getSubdomainParameter } from '@carlito/browser-utils';
import { useCurrentGuest } from '@carlito/hub-react';
import { GET_LOGGED_IN_USER_QUERY } from '../infrastructure/graphql/me.gql';
import { MeQuery } from '../infrastructure/graphql/types/MeQuery';
import { EmployeeDashboardRole } from './useEditHotelEmployeeRole';
import { useGetHotelBySubdomain } from './useGetHotelBySubdomain';

interface UseCurrentUserParams {
  /**
   * If true, autom.
   * refetches JWT if no
   * user is logged in, just
   * to check if a login happened
   * in another tab.
   */
  autoRefetch?: boolean;
}

export function useCurrentUser({ autoRefetch }: UseCurrentUserParams = {}) {
  const {
    data,
    error: errorParam,
    loading: gqlLoading,
    ...rest
  } = useQuery<MeQuery>(GET_LOGGED_IN_USER_QUERY, {
    context: {
      useApolloNetworkStatus: false,
    },
    errorPolicy: 'all',
    pollInterval: autoRefetch ? 2000 : undefined,
  });

  const currentGuestQuery = useCurrentGuest();

  const { hotel: subdomainHotel } = useGetHotelBySubdomain({
    subdomain: getSubdomainParameter('app'),
  });

  const guestHotelId = currentGuestQuery.data?.loggedInGuest?.currentReservation?.hotelId;

  // If there's an error about the profilePicture,
  // that's only because our custom way
  // of authing users with a "passwordless" JWT.
  // Exclude that error from the array
  const error = errorParam
    ? errorParam.message.includes('profilePicture')
      ? undefined
      : errorParam
    : undefined;

  const ownerOf = data?.self?.ownerOf;
  const employeeOf = data?.self?.ownerOf ?? data?.self?.employeeOf;

  const hotelId = employeeOf?.id ?? guestHotelId ?? subdomainHotel?.id ?? '';
  const employeeHotel = employeeOf;
  const user = data?.self;

  const dashboardRole = user?.dashboardRole ?? EmployeeDashboardRole.default;

  const loading =
    gqlLoading || (currentGuestQuery.isLoading && currentGuestQuery.fetchStatus !== 'idle');

  return {
    ...rest,
    loading,
    error,
    user,
    hotelId,
    employeeHotel: employeeHotel,
    ownerOf,
    employeeOf,
    dashboardRole,
  };
}
